import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import logoImage from "/src/logos/logo web 4.svg";
import {
  Rocket,
  BarChart,
  Globe,
  Users,
  Shield,
  Award,
  ArrowRight,
  Building2,
  Stethoscope,
  GraduationCap,
  ShoppingBag,
  Factory,
  Landmark,
  Truck,
  Hotel,
  Briefcase,
  Star,
  TrendingUp,
  CheckCircle2,
  Trophy,
  Target,
  Zap,
  Smartphone,
  Database,
  Quote,
  ChevronDown,
  Plus,
  Minus,
  X,
  Mail,
  Phone,
  MessageSquare
} from 'lucide-react';
import Logo from '../components/Logo';
import Vector from '../components/Vector';
import GrowthChart from '../components/charts/GrowthChart';
import RadialChart from '../components/charts/RadialChart';
import ComparisonChart from '../components/charts/ComparisonChart';

interface ClientLogo {
  id: string;
  name: string;
  logo_url: string;
  display_order: number;
}

export default function Home() {
  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);
  const [showContactModal, setShowContactModal] = React.useState(false);
  const [clientLogos, setClientLogos] = React.useState<ClientLogo[]>([]);
  const [contactForm, setContactForm] = React.useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    fetchClientLogos();
  }, []);

  const fetchClientLogos = async () => {
    try {
      const { data, error } = await supabase
        .from('client_logos')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setClientLogos(data || []);
    } catch (error) {
      console.error('Error fetching client logos:', error);
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Contact form submitted:', contactForm);
    setShowContactModal(false);
    setContactForm({ name: '', email: '', phone: '', message: '' });
  };

  const growthData = [
    { name: '2019', value: 30 },
    { name: '2020', value: 45 },
    { name: '2021', value: 65 },
    { name: '2022', value: 85 },
    { name: '2023', value: 100 },
    { name: '2024', value: 120 }
  ];

  const performanceData = [
    { name: 'Website Speed', value: 90, fill: '#2563eb' },
    { name: 'User Satisfaction', value: 95, fill: '#16a34a' },
    { name: 'Project Delivery', value: 88, fill: '#ea580c' },
    { name: 'Support Response', value: 92, fill: '#8b5cf6' }
  ];

  const comparisonData = [
    { name: 'Traffic', before: 1000, after: 5000 },
    { name: 'Conversion', before: 2, after: 8 },
    { name: 'Revenue', before: 5000, after: 25000 },
    { name: 'Engagement', before: 10, after: 45 }
  ];

  const services = [
    {
      icon: <Globe className="h-8 w-8 text-blue-500" />,
      title: 'Web Development',
      description: 'Custom websites and web applications built for performance and scalability',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2072',
      metric: '500+ Projects'
    },
    {
      icon: <Smartphone className="h-8 w-8 text-blue-500" />,
      title: 'Mobile Apps',
      description: 'Native and cross-platform mobile applications for iOS and Android',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=2072',
      metric: '1M+ Downloads'
    },
    {
      icon: <BarChart className="h-8 w-8 text-blue-500" />,
      title: 'Digital Marketing',
      description: 'SEO, PPC, and social media strategies that drive measurable results',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2072',
      metric: '300% ROI'
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-500" />,
      title: 'Cybersecurity',
      description: 'Enterprise-grade security solutions to protect your digital assets',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=2072',
      metric: '99.9% Uptime'
    },
    {
      icon: <Database className="h-8 w-8 text-blue-500" />,
      title: 'Cloud Services',
      description: 'Scalable cloud infrastructure and migration services',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2072',
      metric: '50% Cost Savings'
    },
    {
      icon: <Zap className="h-8 w-8 text-blue-500" />,
      title: 'AI & Automation',
      description: 'Intelligent automation solutions powered by machine learning',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=2072',
      metric: '70% Efficiency'
    }
  ];

  const industries = [
    {
      icon: <Building2 className="h-12 w-12 text-blue-500" />,
      title: 'Real Estate',
      description: 'Digital solutions for property management and real estate operations',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=800'
    },
    {
      icon: <Stethoscope className="h-12 w-12 text-blue-500" />,
      title: 'Healthcare',
      description: 'Advanced systems for healthcare providers and medical facilities',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=800'
    },
    {
      icon: <GraduationCap className="h-12 w-12 text-blue-500" />,
      title: 'Education',
      description: 'E-learning platforms and educational management systems',
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&q=80&w=800'
    },
    {
      icon: <ShoppingBag className="h-12 w-12 text-blue-500" />,
      title: 'E-Commerce',
      description: 'Powerful online stores and marketplace solutions',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=800'
    },
    {
      icon: <Factory className="h-12 w-12 text-blue-500" />,
      title: 'Manufacturing',
      description: 'Industry 4.0 solutions for modern manufacturing',
      image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&q=80&w=800'
    },
    {
      icon: <Landmark className="h-12 w-12 text-blue-500" />,
      title: 'Finance',
      description: 'Secure fintech solutions and banking systems',
      image: 'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?auto=format&fit=crop&q=80&w=800'
    }
  ];

  const features = [
    {
      icon: <Rocket className="h-8 w-8" />,
      title: 'Rapid Deployment',
      description: 'Go live in weeks, not months with our proven methodology'
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: 'Results-Driven',
      description: 'Average 300% ROI for clients within the first year'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Expert Team',
      description: '200+ certified professionals with 15+ years average experience'
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: 'Scalable Solutions',
      description: 'Built to grow with your business from startup to enterprise'
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Enterprise Security',
      description: 'Bank-level security with ISO 27001 and SOC 2 compliance'
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: '24/7 Support',
      description: 'Dedicated support team available around the clock for your success'
    }
  ];

  const stats = [
    {
      icon: <TrendingUp className="h-6 w-6 text-blue-500" />,
      number: "500+",
      label: "Projects Completed",
      description: "Successfully delivered digital solutions"
    },
    {
      icon: <Users className="h-6 w-6 text-blue-500" />,
      number: "98%",
      label: "Client Satisfaction",
      description: "Based on client feedback and reviews"
    },
    {
      icon: <Trophy className="h-6 w-6 text-blue-500" />,
      number: "50+",
      label: "Industry Awards",
      description: "Recognition for excellence"
    },
    {
      icon: <Star className="h-6 w-6 text-blue-500" />,
      number: "15+",
      label: "Years Experience",
      description: "Delivering digital excellence"
    }
  ];

  const testimonials = [
    {
      quote: "CyberEdge transformed our digital presence completely. Our online revenue increased by 250% in just 6 months.",
      author: "Sarah Johnson",
      role: "CEO, TechStart Inc",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200"
    },
    {
      quote: "The team's expertise in cybersecurity gave us peace of mind. They're true partners in our digital journey.",
      author: "Michael Chen",
      role: "CTO, SecureBank",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200"
    },
    {
      quote: "Best decision we made was partnering with CyberEdge. Their mobile app solution exceeded all expectations.",
      author: "Emily Rodriguez",
      role: "Founder, HealthTrack",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200"
    }
  ];

  const caseStudies = [
    {
      title: "E-Commerce Revolution",
      company: "RetailPro",
      result: "500% increase in online sales",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=800",
      category: "E-Commerce"
    },
    {
      title: "Healthcare Digital Transformation",
      company: "MediCare Plus",
      result: "40% operational efficiency gain",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=800",
      category: "Healthcare"
    },
    {
      title: "FinTech Platform Launch",
      company: "PayFlow",
      result: "1M+ users in first year",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=800",
      category: "Finance"
    }
  ];

  const faqs = [
    {
      question: "What services does CyberEdge offer?",
      answer: "We provide comprehensive digital solutions including web development, mobile apps, digital marketing, cybersecurity, cloud services, and AI & automation. Our full-stack team can handle projects from concept to deployment."
    },
    {
      question: "How long does a typical project take?",
      answer: "Project timelines vary based on scope and complexity. Simple websites can be completed in 2-4 weeks, while enterprise applications may take 3-6 months. We use agile methodology to deliver value quickly with rapid deployments."
    },
    {
      question: "What industries do you specialize in?",
      answer: "We have deep expertise across multiple industries including real estate, healthcare, education, e-commerce, manufacturing, and finance. Our team adapts quickly to new domains with industry-specific best practices."
    },
    {
      question: "Do you provide ongoing support after launch?",
      answer: "Yes, we offer 24/7 support and maintenance packages. This includes security updates, performance monitoring, bug fixes, and feature enhancements. We're committed to your long-term success."
    },
    {
      question: "What is your pricing model?",
      answer: "We offer flexible pricing models including fixed-price projects, time & materials, and dedicated team arrangements. Each project gets a custom quote based on requirements. Contact us for a detailed proposal."
    },
    {
      question: "How do you ensure project security?",
      answer: "Security is our top priority. We follow industry standards including ISO 27001 and SOC 2 compliance, conduct regular security audits, implement encryption, and use secure development practices throughout the project lifecycle."
    }
  ];

  const processSteps = [
    {
      number: "01",
      title: "Discovery & Strategy",
      description: "We start by understanding your business goals, target audience, and technical requirements through comprehensive workshops and analysis.",
      icon: <Target className="h-8 w-8" />
    },
    {
      number: "02",
      title: "Design & Planning",
      description: "Our design team creates intuitive user experiences and detailed technical specifications aligned with your vision.",
      icon: <Briefcase className="h-8 w-8" />
    },
    {
      number: "03",
      title: "Development & Testing",
      description: "Using agile methodology, we build your solution with continuous testing, quality assurance, and regular client feedback.",
      icon: <Rocket className="h-8 w-8" />
    },
    {
      number: "04",
      title: "Launch & Support",
      description: "We handle deployment, provide training, and offer ongoing support to ensure your continued success.",
      icon: <Award className="h-8 w-8" />
    }
  ];

  const techStack = [
    { name: "React", category: "Frontend", logo: "https://cdn.simpleicons.org/react/61DAFB" },
    { name: "Node.js", category: "Backend", logo: "https://cdn.simpleicons.org/nodedotjs/339933" },
    { name: "Python", category: "Backend", logo: "https://cdn.simpleicons.org/python/3776AB" },
    { name: "AWS", category: "Cloud", logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" },
    { name: "Docker", category: "DevOps", logo: "https://cdn.simpleicons.org/docker/2496ED" },
    { name: "PostgreSQL", category: "Database", logo: "https://cdn.simpleicons.org/postgresql/4169E1" },
    { name: "MongoDB", category: "Database", logo: "https://cdn.simpleicons.org/mongodb/47A248" },
    { name: "TypeScript", category: "Language", logo: "https://cdn.simpleicons.org/typescript/3178C6" },
    { name: "Next.js", category: "Framework", logo: "https://cdn.simpleicons.org/nextdotjs/000000" },
    { name: "GraphQL", category: "API", logo: "https://cdn.simpleicons.org/graphql/E10098" },
    { name: "Redis", category: "Cache", logo: "https://cdn.simpleicons.org/redis/DC382D" },
    { name: "Kubernetes", category: "DevOps", logo: "https://cdn.simpleicons.org/kubernetes/326CE5" }
  ];

  return (
    <article className="bg-white">
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden" role="banner">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900">
          <Vector type="dots" className="absolute inset-0 w-full h-full text-white" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
            <div className="absolute top-40 right-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
          </div>
        </div>
        <div className="absolute inset-0">
          <Vector type="wave" className="absolute bottom-0 w-full text-white transform translate-y-1/2" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="animate-float mb-8">
           <img src={logoImage} alt="CyberEdge Logo" className="h-24 w-auto mx-auto object-contain brightness-0 invert drop-shadow-2xl filter contrast-125" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Transform Your Business with
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300 animate-pulse">
              Digital Excellence
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Enterprise-grade solutions trusted by 500+ companies to drive growth, enhance performance, and deliver exceptional digital experiences
          </p>
          <div className="flex flex-wrap justify-center gap-8 mb-12 text-white">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-6 w-6 text-cyan-300" />
              <span className="text-lg">98% Client Satisfaction</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-6 w-6 text-cyan-300" />
              <span className="text-lg">15+ Years Experience</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-6 w-6 text-cyan-300" />
              <span className="text-lg">Award-Winning Team</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="group inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-blue-700 bg-white hover:bg-blue-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-label="Get started with CyberEdge"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
            <button
              onClick={() => setShowContactModal(true)}
              className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-lg text-white hover:bg-white hover:text-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
              aria-label="Contact our sales team"
            >
              Talk to an Expert
            </button>
          </div>
        </div>
      </header>

      <section className="py-16 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100 overflow-hidden" aria-labelledby="clients-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="clients-heading" className="text-center text-gray-600 text-sm font-semibold uppercase tracking-wider mb-12">
            Trusted by Leading Companies
          </h2>
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10"></div>
            <div className="flex overflow-hidden">
              <div className="flex animate-scroll">
                {[...clientLogos, ...clientLogos].map((logo, index) => (
                  <div
                    key={`logo-1-${index}`}
                    className="flex-shrink-0 mx-6 w-80 h-48 flex items-center justify-center bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:scale-105"
                  >
                    <img
                      src={logo.logo_url}
                      alt={logo.name}
                      className="w-full h-full object-contain p-8"
                    />
                  </div>
                ))}
              </div>
              <div className="flex animate-scroll" aria-hidden="true">
                {[...clientLogos, ...clientLogos].map((logo, index) => (
                  <div
                    key={`logo-2-${index}`}
                    className="flex-shrink-0 mx-6 w-80 h-48 flex items-center justify-center bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:scale-105"
                  >
                    <img
                      src={logo.logo_url}
                      alt={logo.name}
                      className="w-full h-full object-contain p-8"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 overflow-hidden" aria-labelledby="services-heading">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2072)',
            filter: 'brightness(0.1)'
          }}
          role="presentation"
        />
        <Vector type="grid" className="absolute inset-0 w-full h-full text-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="services-heading" className="text-3xl md:text-4xl font-bold text-white mb-4">
              Comprehensive Digital Solutions
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Full-spectrum services to accelerate your digital transformation
            </p>
            <Link
              to="/solutions"
              className="inline-flex items-center text-cyan-300 hover:text-cyan-200 font-semibold"
            >
              View All Solutions
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <article
                key={index}
                className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-2"
              >
                <div className="absolute inset-0">
                  <img
                    src={service.image}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
                </div>
                <div className="relative p-8">
                  <div className="absolute top-4 right-4 px-3 py-1 bg-cyan-500/80 backdrop-blur-sm rounded-full text-white text-xs font-semibold">
                    {service.metric}
                  </div>
                  <div className="flex justify-center mb-4" aria-hidden="true">
                    <div className="p-3 bg-white/10 backdrop-blur-sm rounded-lg group-hover:bg-white/20 group-hover:scale-110 transition-all duration-500">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-white text-center mb-4">
                    {service.title}
                  </h3>
                  <p className="text-blue-100 text-center">
                    {service.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden" aria-labelledby="features-heading">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50"></div>
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        <Vector type="dots" className="absolute inset-0 w-full h-full text-blue-100 opacity-30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              <Star className="h-4 w-4 mr-2" />
              Industry-Leading Excellence
            </div>
            <h2 id="features-heading" className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose CyberEdge?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the difference with our award-winning team, cutting-edge technology, and proven track record of delivering exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-white/50"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-cyan-500/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="relative">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-blue-500 rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                      <div className="relative p-5 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                        <div className="text-white">
                          {feature.icon}
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 text-center mb-4 group-hover:text-blue-700 transition-colors duration-300">
                    {feature.title}
                  </h3>

                  <p className="text-gray-600 text-center leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-center text-blue-600 font-semibold text-sm group-hover:text-blue-700 transition-colors">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      <span>Verified Excellence</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-6 bg-white/80 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-xl">
              <div className="flex items-center space-x-3">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100" alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  <div className="w-10 h-10 rounded-full border-2 border-white bg-blue-500 flex items-center justify-center text-white text-sm font-bold">
                    +500
                  </div>
                </div>
                <div className="text-left">
                  <div className="font-bold text-gray-900">500+ Happy Clients</div>
                  <div className="text-sm text-gray-600">Join our success stories</div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" aria-labelledby="case-studies-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="case-studies-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real results from real clients
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <article
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="absolute inset-0">
                  <img
                    src={study.image}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />
                </div>
                <div className="relative p-8 h-80 flex flex-col justify-end">
                  <span className="inline-block px-3 py-1 bg-blue-500/80 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-4 self-start">
                    {study.category}
                  </span>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {study.title}
                  </h3>
                  <p className="text-blue-100 mb-4">{study.company}</p>
                  <div className="flex items-center text-cyan-300 font-bold text-lg">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    {study.result}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/work"
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="industries-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="industries-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600">
              Specialized expertise across diverse business sectors
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <article
                key={index}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="absolute inset-0">
                  <img
                    src={industry.image}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
                </div>
                <div className="relative p-8">
                  <div className="flex justify-center mb-4" aria-hidden="true">
                    <div className="p-3 bg-white/10 backdrop-blur-sm rounded-lg group-hover:scale-110 transition-transform duration-300">
                      {industry.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-white text-center mb-4">
                    {industry.title}
                  </h3>
                  <p className="text-blue-100 text-center">
                    {industry.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50" aria-labelledby="testimonials-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="testimonials-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <article
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <Quote className="h-10 w-10 text-blue-500 mb-4" />
                <p className="text-gray-700 mb-6 italic text-lg">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover mr-4"
                    loading="lazy"
                  />
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.author}</div>
                    <div className="text-gray-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 bg-gradient-to-r from-blue-600 to-blue-800 overflow-hidden" aria-labelledby="stats-heading">
        <Vector type="circles" className="absolute inset-0 w-full h-full text-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="stats-heading" className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Our Impact in Numbers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-xl hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex justify-center mb-4" aria-hidden="true">
                  <div className="p-3 bg-white/10 rounded-lg">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-5xl font-bold text-white text-center mb-2">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-blue-100 text-center mb-2">
                  {stat.label}
                </div>
                <p className="text-blue-100 text-center text-sm">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="performance-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="performance-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Growth & Performance Metrics
            </h2>
            <p className="text-xl text-gray-600">
              Data-driven results that speak for themselves
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Client Growth Over Time</h3>
              <GrowthChart data={growthData} />
              <p className="text-center text-gray-600 mt-4">Consistent year-over-year growth demonstrates our commitment to excellence</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Performance Excellence</h3>
              <RadialChart data={performanceData} />
              <p className="text-center text-gray-600 mt-4">Industry-leading metrics across all key performance indicators</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Before & After Comparison</h3>
            <ComparisonChart data={comparisonData} />
            <p className="text-center text-gray-600 mt-4">Average client improvements after partnering with CyberEdge</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" aria-labelledby="process-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="process-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Proven Process
            </h2>
            <p className="text-xl text-gray-600">
              A systematic approach to delivering exceptional results
            </p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-cyan-400 transform -translate-y-1/2"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-blue-100 hover:border-blue-400">
                    <div className="flex justify-center mb-6">
                      <div className="relative">
                        <div className="absolute inset-0 bg-blue-500 rounded-full blur-lg opacity-20"></div>
                        <div className="relative w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white shadow-lg">
                          {step.icon}
                        </div>
                      </div>
                    </div>
                    <div className="text-center mb-4">
                      <div className="text-5xl font-bold text-blue-200 mb-2">{step.number}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    </div>
                    <p className="text-gray-600 text-center leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-12">
            <Link
              to="/about"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Learn More About Our Process
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="tech-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="tech-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Technology Stack
            </h2>
            <p className="text-xl text-gray-600">
              Building with industry-leading technologies and frameworks
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {techStack.map((tech, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-blue-100 hover:border-blue-400"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative text-center">
                  <div className="flex justify-center mb-3">
                    <img
                      src={tech.logo}
                      alt={`${tech.name} logo`}
                      className="h-16 w-16 object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {tech.name}
                  </div>
                  <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold group-hover:bg-blue-200 transition-colors">
                    {tech.category}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              We stay current with the latest technologies to deliver cutting-edge solutions
            </p>
            <Link
              to="/solutions"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Explore Our Solutions
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about working with CyberEdge
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-xl"
                  aria-expanded={openFaqIndex === index}
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <Minus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <Plus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Contact Our Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="relative py-20 overflow-hidden" aria-labelledby="cta-heading">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2072)',
            filter: 'brightness(0.3)'
          }}
          role="presentation"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/50 to-blue-800/50" />
        <Vector type="wave" className="absolute bottom-0 w-full text-white transform translate-y-1/2" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
              Join 500+ companies that trust CyberEdge to drive their digital success
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-blue-700 bg-white hover:bg-blue-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Start your journey with CyberEdge"
              >
                Start Your Journey
                <ArrowRight className="ml-2 h-6 w-6" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-lg text-white hover:bg-white hover:text-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                aria-label="Schedule a demo with our team"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {showContactModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
              onClick={() => setShowContactModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowContactModal(false)}
                  className="bg-white rounded-full p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-cyan-600 px-6 py-8 sm:px-10">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-full">
                    <MessageSquare className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-white text-center mb-2" id="modal-title">
                  Contact Our Team
                </h3>
                <p className="text-blue-100 text-center">
                  Fill out the form below and we'll get back to you within 24 hours
                </p>
              </div>

              <form onSubmit={handleContactSubmit} className="px-6 py-8 sm:px-10">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={contactForm.email}
                        onChange={handleContactFormChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactFormChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your project..."
                    ></textarea>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    className="flex-1 inline-flex justify-center items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-lg hover:shadow-xl"
                  >
                    Send Message
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowContactModal(false)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>

                <p className="mt-6 text-sm text-gray-500 text-center">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </form>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
